// Drobné prvky sales sekce podle schváleného mockupu v8 (30. 9. 2026).
// Tóny jen ze src/lib/tony.js, oddělovače v pilulkách svislou čárou.
import React, { useEffect, useState } from "react";
import { ArrowUpRight, ChevronDown, Mic, NotebookPen, Info, CheckCircle2, AlertTriangle, CalendarClock, Check } from "lucide-react";
import { ton as tonPodleKlice } from "../../lib/tony.js";

/** Pilulka s částmi oddělenými svislou čárou. casti: string | string[] */
export function Pil({ ton = "neutralni", casti, ikona: Ikona, mala = false }) {
  const t = tonPodleKlice(ton);
  const seznam = [].concat(casti).filter((x) => x !== null && x !== undefined && x !== "");
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-bold ${mala ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-[13px]"} ${t.pilulka}`}>
      {Ikona && <Ikona className="h-3.5 w-3.5 shrink-0" />}
      {seznam.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden className="h-3 w-px shrink-0 bg-current opacity-40" />}
          {c}
        </React.Fragment>
      ))}
    </span>
  );
}

/** Nadpis skupiny v kartě (světle fialový pruh). */
export function SkupinaNadpis({ children, prava }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl bg-brand-100 px-3 py-2">
      <span className="text-[11px] font-bold uppercase tracking-wider text-brand-deep">{children}</span>
      {prava}
    </div>
  );
}

/** Číslovaná část kroku průvodce (1. 10. 2026, mockup „Pravidelné časy v hovoru“): Ověř dotazník, Nabídni čas. */
export function CastKroku({ cislo, nazev, hotovo = false, children, className = "" }) {
  return (
    <div className={`space-y-3 rounded-2xl border border-slate-200 p-3 sm:p-4 ${hotovo ? "bg-brand-50/40" : "bg-white"} ${className}`}>
      <div className="flex items-center gap-2.5">
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white ${hotovo ? "bg-emerald-600" : "bg-brand"}`}>
          {hotovo ? <Check className="h-4 w-4" /> : cislo}
        </span>
        <span className="text-[15px] font-bold text-slate-900">{nazev}</span>
      </div>
      {children}
    </div>
  );
}

/** Pruh skupiny v seznamu (SekcePruh, otevřený). */
export function PruhSkupiny({ title, pocet, ton = "znacka" }) {
  const t = tonPodleKlice(ton);
  return (
    <div className={`flex min-h-[48px] w-full items-center gap-2 px-4 py-2 ${t.pole}`}>
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${t.plny}`} />
      <span className={`min-w-0 flex-1 truncate text-[11px] font-bold uppercase tracking-wider ${t.text}`}>{title}</span>
      {pocet != null && <span className={`shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold tabular-nums ${t.text}`}>{pocet}</span>}
    </div>
  );
}

/** Dlaždice údaje z dotazníku. */
export function DotTile({ popisek, hodnota, cely = false }) {
  return (
    <div className={`min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 ${cely ? "col-span-2 lg:col-span-3" : ""}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{popisek}</div>
      <div className="whitespace-pre-line text-sm font-bold leading-snug text-slate-800 [overflow-wrap:anywhere]">{hodnota || "neuvedeno"}</div>
    </div>
  );
}

/** Řádek kontaktu v seznamu: jméno, pilulky, poznámka, tlačítko Otevřít.
 *  akce = volitelná tlačítka vlevo od tlačítka Otevřít (např. smazání v Knize úrazů). */
export function RadekKontaktu({ jmeno, pilulky, poznamka, vybrany = false, onOtevrit, prvni = false, akce = null, children }) {
  return (
    <div className={`flex items-center gap-3 px-4 py-3 ${prvni ? "" : "border-t border-slate-100"} ${vybrany ? "bg-brand-50 lg:shadow-[inset_4px_0_0_#7C5DB0]" : "bg-white"}`}>
      <div className="min-w-0 flex-1">
        <div className="text-base font-bold leading-snug text-slate-900">{jmeno}</div>
        {pilulky && <div className="mt-1.5 flex flex-wrap gap-1.5">{pilulky}</div>}
        {poznamka && (
          <div className="mt-1.5 flex items-start gap-1.5 text-[13px] leading-snug text-slate-600">
            <NotebookPen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="min-w-0">{poznamka}</span>
          </div>
        )}
        {children}
      </div>
      {akce}
      {onOtevrit && (
        <button type="button" onClick={onOtevrit} className="btn-ikona" aria-label={`Otevřít kartu ${jmeno}`} title="Otevřít kartu">
          <ArrowUpRight className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

/** Barevná hláška s ikonou (info, znacka, ok, pozor, problem). */
export function Oznameni({ ton = "info", ikona, children, className = "" }) {
  const c = {
    info: "border-sky-200 bg-sky-50 text-sky-900", ok: "border-emerald-200 bg-emerald-50 text-emerald-900",
    znacka: "border-brand-200 bg-brand-50 text-brand-950", pozor: "border-amber-200 bg-amber-50 text-amber-900", problem: "border-rose-200 bg-rose-50 text-rose-900",
  }[ton] || "border-sky-200 bg-sky-50 text-sky-900";
  const Ikona = ikona || (ton === "ok" ? CheckCircle2 : ton === "problem" || ton === "pozor" ? AlertTriangle : ton === "znacka" ? CalendarClock : Info);
  return (
    <div className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 ${c} ${className}`}>
      <Ikona className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 text-[14px] leading-snug">{children}</div>
    </div>
  );
}

/** "Co říct" z call scriptu, rozbalovací. Výchozí stav rozbalený. */
export function CoRict({ children, vychoziOtevreno = true }) {
  const [otevreno, setOtevreno] = useState(vychoziOtevreno);
  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50">
      <button type="button" onClick={() => setOtevreno((o) => !o)} aria-expanded={otevreno}
        className="flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-[13px] font-bold uppercase tracking-wide text-brand-deep">
        <Mic className="h-4 w-4 shrink-0" />
        <span className="flex-1">Co říct</span>
        <ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${otevreno ? "" : "-rotate-90"}`} />
      </button>
      {otevreno && <div className="border-t border-brand-200 px-3 py-2.5 text-[15px] leading-relaxed text-brand-950">{children}</div>}
    </div>
  );
}

/** Výběr jedné možnosti čipy (cip, aria-pressed). */
export function Cipy({ moznosti, value, onChange, vice = false }) {
  const vybrane = vice ? (Array.isArray(value) ? value : []) : [value];
  return (
    <div className="flex flex-wrap gap-2">
      {moznosti.map((m) => {
        const klic = typeof m === "string" ? m : m.key;
        const popis = typeof m === "string" ? m : m.label;
        const zap = vybrane.includes(klic);
        return (
          <button key={klic} type="button" className="cip" aria-pressed={zap}
            onClick={() => onChange(vice ? (zap ? vybrane.filter((x) => x !== klic) : [...vybrane, klic]) : (zap ? "" : klic))}>
            {popis}
          </button>
        );
      })}
    </div>
  );
}

/** Svislý seznam voleb s kolečkem (radio). */
export function Volby({ moznosti, value, onChange, name }) {
  return (
    <div className="space-y-2" role="radiogroup" aria-label={name}>
      {moznosti.map((m) => {
        const zap = m.key === value;
        return (
          <button key={m.key} type="button" role="radio" aria-checked={zap} onClick={() => onChange(m.key)}
            className={`flex min-h-[52px] w-full items-center gap-3 rounded-xl border px-3 text-left ${zap ? "border-2 border-brand bg-brand-50" : "border-slate-300 bg-white"}`}>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${zap ? "border-brand" : "border-slate-300"}`}>
              {zap && <span className="h-2.5 w-2.5 rounded-full bg-brand" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className={`block text-[15px] font-bold ${zap ? "text-brand-deep" : "text-slate-900"}`}>{m.label}</span>
              {m.popis && <span className="block text-[13px] text-slate-500">{m.popis}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Pole s výrazným nadpisem a ikonou (Cíl, Zdraví). */
export function PoleSIkonou({ popisek, ikona: Ikona, hint, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-deep"><Ikona className="h-[18px] w-[18px]" /></span>
        <span className="text-[16px] font-bold text-slate-900">{popisek}</span>
      </div>
      {children}
      {hint && <span className="block text-[13px] text-slate-500">{hint}</span>}
    </div>
  );
}

/** Seznam hotových kroků se zelenou fajfkou. */
export function Fajfky({ polozky }) {
  return (
    <div className="space-y-2">
      {polozky.map((p, i) => (
        <div key={i} className="flex items-start gap-2.5 text-[15px] text-slate-800">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          <span>{p}</span>
        </div>
      ))}
    </div>
  );
}

/** Řádek shrnutí: popisek vlevo, hodnota tučně vpravo. */
export function RadekShrnuti({ popisek, children }) {
  return (
    <div className="flex items-start justify-between gap-4 border-t border-slate-100 py-2.5 first:border-t-0">
      <span className="text-[14px] text-slate-500">{popisek}</span>
      <span className="text-right text-[15px] font-bold text-slate-900 [overflow-wrap:anywhere]">{children}</span>
    </div>
  );
}

/** Obal karty se seznamem. */
export function KartaObal({ children, className = "" }) {
  return <div className={`glass min-w-0 overflow-hidden !p-0 ${className}`}>{children}</div>;
}

/** Karta s tmavou lištou (nadpis s ikonou). */
export function KartaLista({ ikona: Ikona, nazev, akce, children, className = "" }) {
  return (
    <div className={`glass min-w-0 overflow-hidden p-4 sm:p-5 ${className}`}>
      <div className="-mx-4 -mt-4 mb-4 flex items-center gap-3 bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3 text-white sm:-mx-5 sm:-mt-5 sm:mb-5 sm:px-5">
        {Ikona && <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20"><Ikona className="h-5 w-5" /></span>}
        <h3 className="min-w-0 flex-1 font-serif text-lg font-bold leading-tight">{nazev}</h3>
        {akce}
      </div>
      {children}
    </div>
  );
}

/** Na počítači (od 1024 px) se karta kontaktu kreslí vedle seznamu. */
export function useSiroka() {
  const dotaz = "(min-width: 1024px)";
  const [siroka, setSiroka] = useState(() => (typeof window === "undefined" ? true : window.matchMedia(dotaz).matches));
  useEffect(() => {
    const mq = window.matchMedia(dotaz);
    const z = (e) => setSiroka(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", z); else mq.addListener(z);
    setSiroka(mq.matches);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", z); else mq.removeListener(z); };
  }, []);
  return siroka;
}

/** Kontakt, který je nebo byl klientkou (2. 10. 2026): trenér a stav spolupráce, třeba „Tomáš Janata | přerušeno“. */
export function SpolupracePil({ k, mala = false }) {
  const kl = k?.klientka;
  if (!kl) return null;
  const ton = kl.stav === "Aktivní" ? "ok" : kl.stav === "Přerušeno" ? "pozor" : "zlata";
  return <Pil ton={ton} casti={[kl.trener || "klientka", String(kl.stav || "").toLowerCase()]} mala={mala} />;
}

/** Stav kontaktu jako pilulka. */
export function StavPilulka({ k, mala = false }) {
  if (!k) return null;
  // 22. 10. 2026: nedorazila na zkušební (s omluvou čeká na hovor, bez omluvy je NQ lead).
  if (k.nedorazila && ["novy", "pokus", "odlozeno", "doplnit"].includes(k.stav)) return <Pil ton="pozor" casti={["Nedorazila na zkušební", "s omluvou"]} mala={mala} />;
  if (k.nedorazila && k.stav === "nezajem") return <Pil ton="problem" casti={["NQlead", "nedorazila bez omluvy"]} mala={mala} />;
  if (k.stav === "novy") return <Pil ton={k.byvala ? "zlata" : "znacka"} casti={k.byvala ? ["bývalá klientka", "k volání"] : ["Nová", "1. hovor"]} mala={mala} />;
  if (k.stav === "pokus") return <Pil ton={k.pokus >= 3 ? "problem" : "pozor"} casti={`${k.pokus + 1}. pokus ze 4`} mala={mala} />;
  if (k.stav === "odlozeno") return <Pil ton="info" casti="Odloženo" mala={mala} />;
  if (k.stav === "doplnit") return <Pil ton="pozor" casti="Doplnit datum" mala={mala} />;
  if (k.stav === "zkusebni") return <Pil ton="ok" casti="Zkušební" mala={mala} />;
  if (k.stav === "klientka") return <Pil ton="ok" casti="Klientka" mala={mala} />;
  if (k.stav === "nezajem") return <Pil ton="neutralni" casti={["NQlead", k.duvod_text || ""]} mala={mala} />;
  if (k.stav === "neuzavreno") return <Pil ton="pozor" casti="Neuzavřena po zkušebním" mala={mala} />;
  return <Pil ton="neutralni" casti={k.stage || "Mimo sales"} mala={mala} />;
}
